import ContentContainer from '@/features/shared/ContentContainer';
import { MuiContainer } from '@/features/shared/material-ui';
import { AppContext } from '@/store';
import { updateHeaderHeight } from '@/store/header/actions';
import { ColorNames, InsetTop, MaxWidth, SpacingSizes } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import CardContent from './CardContent';
import { HeroProps } from './Hero.interface';
import useStyles from './Hero.style';
import HomepageContent from './HomepageContent';
import LandingPageContent from './LandingPageContent';

const isParallaxTemplate = (template = '') => /parallax$/i.test(template);

const mapTemplateToComponent = (template = '') => {
  if (isParallaxTemplate(template)) {
    return HomepageContent;
  }

  if (/card$/i.test(template)) {
    return CardContent;
  }

  return LandingPageContent;
};

const Hero = ({
  content,
  presentation,
  contentButtons,
  children,
  previewMode,
  labels,
}: PropsWithChildren<Br.PropsWithContentButtons<HeroProps>>) => {
  const { backgroundImage, mobileBackgroundImage, backgroundVideo } = content;
  const {
    template,
    contentAlignment,
    backgroundOverlayOpacity,
    containerBottomSpacing,
    containerBottomBackgroundColor,
    isBeneathNavigation,
    hasTextShadow,
    includeJumplinks,
    isParallax,
    overflowContent,
    backgroundColor,
    textColor,
    fullWidthContent,
  } = presentation;
  const { dispatch } = useContext(AppContext);
  const isCard = useMemo(() => template && /card$/i.test(template), [template]);
  const isHomepage = useMemo(() => isParallaxTemplate(template), [template]);
  const theme = useMemo(
    () =>
      resolveTheme(isCard ? 'bg-color' : 'text-color')({
        rawVal: isCard ? containerBottomBackgroundColor : textColor,
      }),
    [containerBottomBackgroundColor, isCard, textColor],
  );
  const classes = useStyles({});
  const containerProps = useMemo(() => {
    const bottomBackgroundColor = isCard ? containerBottomBackgroundColor : undefined;
    const defaultBackgroundColor = backgroundColor || ColorNames.blue;
    const hasVideoOrImage = backgroundVideo || backgroundImage || mobileBackgroundImage;

    const topBackgroundStyles = backgroundColor ? { background: backgroundColor } : {};
    const bottomBackgroundStyles =
      backgroundColor && !isCard ? { background: backgroundColor } : {};

    return {
      topBackgroundStyles,
      bottomBackgroundStyles,
      topBackgroundColor: defaultBackgroundColor,
      bottomBackgroundColor: isHomepage
        ? defaultBackgroundColor
        : bottomBackgroundColor || defaultBackgroundColor,
      bottomSpacing: isCard ? containerBottomSpacing : SpacingSizes.none,
      offsetTop: Boolean(isCard),
      insetTop: isCard ? InsetTop.hero : undefined,
      backgroundImage: backgroundImage || mobileBackgroundImage,
      mobileBackgroundImage: mobileBackgroundImage || backgroundImage,
      backgroundVideo,
      overlay: hasVideoOrImage
        ? {
            hasTextShadow: !isCard && hasTextShadow,
            opacity: backgroundOverlayOpacity,
            alignment: contentAlignment,
            maxWidth: MaxWidth.large,
          }
        : undefined,
      // TODO: this option is outdated, requires test before applying it.
      // isBackgroundSticky: isParallaxTemplate(template) && !isParallax,
    };
  }, [
    backgroundColor,
    backgroundImage,
    backgroundOverlayOpacity,
    backgroundVideo,
    containerBottomBackgroundColor,
    containerBottomSpacing,
    contentAlignment,
    hasTextShadow,
    isCard,
    isHomepage,
    mobileBackgroundImage,
  ]);

  useEffect(() => {
    dispatch(updateHeaderHeight({ height: isBeneathNavigation ? 0 : 'auto' }));
  }, [dispatch, isBeneathNavigation]);

  const Variant = useMemo(() => mapTemplateToComponent(template), [template]);

  const muiContainerClasses = useMemo(
    () =>
      clsx(
        classes.container,
        template === 'homepage-parallax' && 'parallax-height',
        template === 'homepage-large' && 'large-height',
        template === 'landing-medium' && 'medium-height',
        template === 'value-small' && 'small-height',
        template === 'hero-xsmall' && 'xsmall-height',
        template === 'search' && 'search-height',
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template],
  );

  return (
    <ContentContainer {...containerProps} className={classes.contentContainer}>
      <MuiContainer className={muiContainerClasses} component="section">
        <Variant
          {...content}
          theme={theme}
          isParallax={isParallax}
          previewMode={previewMode}
          overflowContent={overflowContent}
          includeJumplinks={includeJumplinks}
          overlayColor={containerBottomBackgroundColor}
          alignment={isCard ? 'left' : contentAlignment}
          fullWidthContent={fullWidthContent}
          contentButtons={contentButtons}
          labels={labels}
        >
          {children}
        </Variant>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(Hero);
